<template>
  <div class="page-outer">
    <div class="page-header page-header-bindings-detail">
      <div class="header-left">
        <h1 class="page-title">好友綁定詳情</h1>
      </div>
      <div class="header-actions">
        <button
          class="btn btn-danger"
          :disabled="deleting || loading"
          @click="handleDeleteRecord"
        >
          <i class="ri-delete-bin-line"></i>
          {{ deleting ? '刪除中...' : '刪除紀錄' }}
        </button>
        <button class="btn btn-secondary" @click="handleBack">返回列表</button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回列表</button>
    </div>

    <div v-else-if="binding" class="detail-container">
      <!-- Tab Navigation -->
      <div class="tabs-nav">
        <button 
          class="tab-btn"
          :class="{ active: activeTab === 'basic' }"
          @click="activeTab = 'basic'"
        >
          <i class="ri-information-line"></i>
          基本資訊
        </button>
        <button 
          v-if="hasMember"
          class="tab-btn"
          :class="{ active: activeTab === 'tags' }"
          @click="activeTab = 'tags'"
        >
          <i class="ri-price-tag-3-line"></i>
          標籤管理
        </button>
        <button 
          v-if="binding && binding.lineUserId"
          class="tab-btn"
          :class="{ active: activeTab === 'coupons' }"
          @click="activeTab = 'coupons'"
        >
          <i class="ri-coupon-line"></i>
          票券
          <span v-if="couponPagination && couponPagination.totalItems > 0" class="tab-count">{{ couponPagination.totalItems }}</span>
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tabs-content">
        <!-- 基本資訊 Tab -->
        <div v-show="activeTab === 'basic'" class="tab-pane">
      <!-- 基本資訊 -->
      <div class="detail-section">
        <h3>基本資訊</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>會員編號</label>
            <div>
              <span v-if="binding.member && binding.member.memberCode">{{ binding.member.memberCode }}</span>
              <span v-else-if="binding.memberCode">{{ binding.memberCode }}</span>
              <span v-else class="text-muted">未綁定會員</span>
            </div>
          </div>
          <div class="detail-item">
            <label>LINE User ID</label>
            <div>
              <code>{{ binding.lineUserId || '-' }}</code>
            </div>
          </div>
          <div class="detail-item">
            <label>LINE 顯示名稱</label>
            <div class="profile-field">
              <span>{{ binding.lineDisplayName || '-' }}</span>
              <button 
                class="btn btn-sm btn-link refresh-btn" 
                @click="handleRefreshProfile"
                :disabled="refreshing"
                title="從 LINE 伺服器刷新最新的顯示名稱和頭像"
              >
                <i :class="refreshing ? 'ri-loader-4-line rotating' : 'ri-refresh-line'"></i>
                {{ refreshing ? '更新中...' : '刷新' }}
              </button>
            </div>
          </div>
          <div class="detail-item" v-if="binding.linePictureUrl">
            <label>LINE 頭像</label>
            <div class="profile-field">
              <img :src="binding.linePictureUrl" alt="LINE 頭像" class="avatar-image" />
            </div>
          </div>
          <div class="detail-item" v-if="binding.profileUpdatedAt">
            <label>Profile 更新時間</label>
            <div>{{ formatDate(binding.profileUpdatedAt) }}</div>
          </div>
          <div class="detail-item">
            <label>綁定來源</label>
            <div>{{ formatSource(binding.sourceType) }}</div>
          </div>
          <div class="detail-item">
            <label>來源代碼</label>
            <div>{{ binding.sourceCode || '-' }}</div>
          </div>
          <div class="detail-item">
            <label>好友狀態</label>
            <div>
              <span :class="getFriendStatusBadgeClass(binding.isFollowing)">
                {{ getFriendStatusText(binding.isFollowing) }}
              </span>
            </div>
          </div>
          <div class="detail-item">
            <label>推播狀態</label>
            <div>
              <span :class="getPushStatusBadgeClass(binding)">
                {{ getPushStatusText(binding) }}
              </span>
            </div>
          </div>
          <div class="detail-item" v-if="binding.blockCount > 0">
            <label>推播失敗次數</label>
            <div>{{ binding.blockCount }} 次</div>
          </div>
        </div>
      </div>

      <!-- 時間資訊 -->
      <div class="detail-section">
        <h3>時間資訊</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>加好友時間</label>
            <div>{{ formatDate(binding.followedAt) }}</div>
          </div>
          <div class="detail-item" v-if="binding.unfollowedAt">
            <label>封鎖／解除好友時間</label>
            <div>{{ formatDate(binding.unfollowedAt) }}</div>
          </div>
          <div class="detail-item" v-if="binding.blockedAt">
            <label>最後推播失敗時間</label>
            <div>{{ formatDate(binding.blockedAt) }}</div>
          </div>
          <div class="detail-item" v-if="binding.lastUnblockedAt">
            <label>推播恢復時間</label>
            <div>{{ formatDate(binding.lastUnblockedAt) }}</div>
          </div>
          <div class="detail-item" v-if="binding.profileUpdatedAt">
            <label>資料更新時間</label>
            <div>{{ formatDate(binding.profileUpdatedAt) }}</div>
          </div>
          <div class="detail-item">
            <label>建立時間</label>
            <div>{{ formatDate(binding.createdAt) }}</div>
          </div>
          <div class="detail-item">
            <label>更新時間</label>
            <div>{{ formatDate(binding.updatedAt) }}</div>
          </div>
        </div>
      </div>

      <!-- 自動標籤 -->
      <div class="detail-section" v-if="binding.autoTags && binding.autoTags.length > 0">
        <h3>自動標籤</h3>
        <div class="tags-display">
          <span 
            v-for="tag in binding.autoTags" 
            :key="tag" 
            class="tag-badge"
          >
            {{ tag }}
          </span>
        </div>
      </div>

      <!-- 其他資訊 -->
      <div class="detail-section">
        <h3>其他資訊</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>LINE 官方帳號</label>
            <div>
              <span v-if="binding.lineConfigName">{{ binding.lineConfigName }}</span>
              <code v-else>{{ binding.messagingChannelId || '-' }}</code>
            </div>
          </div>

        </div>
      </div>
        </div>

        <!-- 標籤管理 Tab -->
        <div v-show="activeTab === 'tags'" class="tab-pane">
          <div v-if="!canLoadTags" class="info-section">
            <div class="empty-state">
              <p>無法載入標籤：缺少 LINE User ID 或 Messaging Channel ID</p>
            </div>
          </div>
          <div v-else class="info-section">
            <div class="section-header">
              <h3 class="section-title">好友標籤</h3>
              <button class="btn btn-primary btn-sm" @click="openAddTagModal">
                <i class="ri-add-line"></i>
                新增標籤
              </button>
            </div>

            <div v-if="tagsLoading" class="loading-state">
              <div class="spinner-sm"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="tagsError" class="error-state">
              <p class="error-message">{{ tagsError }}</p>
            </div>
            <div v-else-if="memberTags.length > 0" class="tags-list">
              <div v-for="tag in memberTags" :key="tag.id" class="tag-card">
                <div class="tag-info">
                  <i v-if="tag.tagIcon" :class="tag.tagIcon" class="tag-icon"></i>
                  <span class="tag-name" :style="{ color: tag.tagColor }">{{ tag.tagName }}</span>
                  <!-- 顯示標籤濃度（僅互動標籤且濃度 > 1） -->
                  <span 
                    v-if="tag.tagSource === 'manual' && tag.intensity && tag.intensity > 1" 
                    class="tag-intensity"
                    :title="`標籤濃度：${tag.intensity} 次貼標`"
                  >
                    x{{ tag.intensity }}
                  </span>
                  <!-- 標籤類型 badge -->
                  <span v-if="tag.tagSource === 'manual'" class="tag-type-badge manual-type">互動</span>
                  <span v-else-if="tag.tagSource === 'auto_rule'" class="tag-type-badge auto-type">自動</span>
                  <span v-else-if="tag.tagSource === 'system'" class="tag-type-badge system-type">系統</span>
                  <!-- 到期日期 -->
                  <span v-if="tag.expiryDate" class="tag-expiry">
                    {{ formatDate(tag.expiryDate) }} 到期
                  </span>
                </div>
                <button class="btn-remove" @click="removeTag(tag.tagId)" :title="`移除標籤：${tag.tagName}`">
                  <i class="ri-close-line"></i>
                </button>
              </div>
            </div>
            <div v-else class="empty-state">
              <p>暫無標籤</p>
            </div>
          </div>
        </div>
        <!-- 票券 Tab -->
        <div v-show="activeTab === 'coupons'" class="tab-pane">
          <div class="section-header">
            <h3 class="section-title">好友票券</h3>
            <button class="btn btn-primary btn-sm" @click="showIssueCouponModal = true">
              <i class="ri-add-line"></i>
              發放優惠券
            </button>
          </div>

          <!-- 篩選 -->
          <div class="coupon-filter">
            <select v-model="couponFilterStatus" class="form-control form-control-sm" @change="loadCoupons(1)">
              <option value="">全部狀態</option>
              <option value="unused">未使用</option>
              <option value="used">已使用</option>
              <option value="expired">已過期</option>
              <option value="cancelled">已取消</option>
              <option value="deleted">已刪除</option>
            </select>
          </div>

          <div v-if="couponsLoading" class="loading-state">
            <div class="spinner-sm"></div>
            <p>載入中...</p>
          </div>
          <div v-else-if="couponsError" class="error-state">
            <p class="error-message">{{ couponsError }}</p>
          </div>
          <div v-else-if="coupons.length === 0" class="empty-state">
            <p>此好友目前沒有票券記錄</p>
          </div>
          <div v-else class="coupons-table-wrapper">
            <table class="coupons-table">
              <thead>
                <tr>
                  <th>票券代碼</th>
                  <th>票券名稱</th>
                  <th>狀態</th>
                  <th>有效期限</th>
                  <th>發放時間</th>
                  <th>使用時間</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="coupon in coupons" :key="coupon.id">
                  <td><code class="coupon-code">{{ coupon.couponCode }}</code></td>
                  <td>{{ coupon.templateName || '-' }}</td>
                  <td>
                    <span :class="getCouponStatusClass(coupon.status)">
                      {{ getCouponStatusText(coupon.status) }}
                    </span>
                    <div v-if="coupon.cancelReason" class="cancel-reason-text">
                      {{ coupon.cancelReason }}
                    </div>
                  </td>
                  <td>{{ coupon.validUntil ? formatDate(coupon.validUntil) : '無限期' }}</td>
                  <td>{{ formatDate(coupon.issuedAt) }}</td>
                  <td>{{ coupon.usedAt ? formatDate(coupon.usedAt) : '-' }}</td>
                  <td>
                    <div class="action-buttons">
                      <button
                        class="btn btn-sm btn-info"
                        @click="viewCouponDetail(coupon)"
                      >
                        <i class="ri-eye-line"></i>
                        詳情
                      </button>
                      <button
                        v-if="coupon.status === 'unused'"
                        class="btn btn-sm btn-danger"
                        @click="handleCancelCoupon(coupon)"
                      >
                        <i class="ri-close-circle-line"></i>
                        取消
                      </button>
                      <button
                        v-if="coupon.status !== 'used' && coupon.status !== 'cancelled' && coupon.status !== 'deleted'"
                        class="btn btn-sm btn-outline-danger"
                        @click="handleDeleteCoupon(coupon)"
                      >
                        <i class="ri-delete-bin-line"></i>
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- 分頁 -->
            <ListPagination
              v-if="couponPagination && couponPagination.totalPages > 1"
              :page="couponPagination.page"
              :total-pages="couponPagination.totalPages"
              :total="couponPagination.totalItems"
              @change="loadCoupons"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-else class="error-container">
      <p class="error-message">找不到綁定記錄</p>
      <button class="btn btn-secondary" @click="handleBack">返回列表</button>
    </div>

    <!-- 新增標籤 Modal -->
    <div v-if="showAddTagModal" class="modal-overlay" @click="showAddTagModal = false">
      <div class="modal-content modal-md" @click.stop>
        <div class="modal-header">
          <h3>新增標籤</h3>
          <button class="modal-close" @click="showAddTagModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <!-- 已選擇的標籤 -->
          <div v-if="selectedTagIds.length > 0" class="selected-tags-container">
            <label>已選擇 ({{ selectedTagIds.length }})</label>
            <div class="selected-tags">
              <span 
                v-for="tagId in selectedTagIds" 
                :key="tagId"
                class="selected-tag-item"
              >
                <i :class="getTagIcon(tagId)" :style="{ color: getTagColor(tagId) }"></i>
                {{ getTagName(tagId) }}
                <button 
                  type="button"
                  class="remove-tag-btn"
                  @click="removeFromSelection(tagId)"
                >
                  <i class="ri-close-line"></i>
                </button>
              </span>
            </div>
          </div>

          <!-- 篩選框 -->
          <div class="form-group">
            <label>篩選標籤</label>
            <input 
              v-model="tagSearchQuery"
              type="text"
              class="form-control"
              placeholder="輸入標籤名稱篩選..."
              @input="filterTags"
            />
          </div>

          <!-- 標籤來源頁籤 -->
          <div class="tag-tabs">
            <button 
              type="button"
              class="tag-tab"
              :class="{ active: activeTagTab === 'all' }"
              @click="activeTagTab = 'all'; filterTags()"
            >
              <i class="ri-list-check"></i>
              全部標籤
            </button>
            <button 
              type="button"
              class="tag-tab"
              :class="{ active: activeTagTab === 'manual' }"
              @click="activeTagTab = 'manual'; filterTags()"
            >
              <i class="ri-hand-heart-line"></i>
              互動標籤
            </button>
            <button 
              type="button"
              class="tag-tab"
              :class="{ active: activeTagTab === 'auto_rule' }"
              @click="activeTagTab = 'auto_rule'; filterTags()"
            >
              <i class="ri-robot-line"></i>
              自動標籤
            </button>
          </div>

          <!-- 可選標籤列表 -->
          <div class="form-group">
            <label>可選標籤 ({{ filteredAvailableTags.length }})</label>
            <p class="form-hint" style="margin-bottom: 12px; color: #666;">
              <i class="ri-information-line"></i> 
              互動標籤可重複貼標，用於累積標籤濃度
            </p>
            <div class="tags-list">
              <div 
                v-for="tag in filteredAvailableTags" 
                :key="tag.id"
                class="tag-list-item"
                :class="{ 
                  'selected': selectedTagIds.includes(tag.id),
                  'disabled': isTagAlreadyAdded(tag.id),
                  'manual-tag': tag.tagSource === 'manual'
                }"
                @click="toggleTagSelection(tag.id)"
              >
                <div class="tag-info">
                  <i :class="tag.tagIcon" :style="{ color: tag.tagColor }"></i>
                  <span class="tag-name">{{ tag.tagName }}</span>
                  <span v-if="tag.tagSource === 'manual'" class="tag-badge manual-badge">互動</span>
                  <span v-else-if="tag.tagSource === 'auto_rule'" class="tag-badge auto-badge">自動</span>
                  <span v-if="tag.isTemporary" class="tag-badge temp-badge">限時</span>
                  <span v-if="isTagAlreadyAdded(tag.id)" class="tag-badge added-badge">已添加</span>
                </div>
                <div v-if="selectedTagIds.includes(tag.id)" class="check-icon">
                  <i class="ri-check-line"></i>
                </div>
              </div>
              <div v-if="filteredAvailableTags.length === 0" class="empty-state">
                <p>{{ tagSearchQuery ? '找不到符合的標籤' : '沒有可用標籤' }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showAddTagModal = false">取消</button>
          <button 
            class="btn btn-primary" 
            :disabled="addingTag || selectedTagIds.length === 0" 
            @click="addMultipleTags"
          >
            {{ addingTag ? '處理中...' : `確定新增 (${selectedTagIds.length})` }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 票券詳情 Modal（共用元件）-->
  <CouponDetailModal
    :visible="showCouponDetailModal"
    :coupon="selectedCoupon"
    @close="showCouponDetailModal = false"
    @cancel="(c) => { showCouponDetailModal = false; handleCancelCoupon(c); }"
    @delete="(c) => { showCouponDetailModal = false; handleDeleteCoupon(c); }"
  />

  <!-- 發放優惠券 Modal -->
  <div v-if="showIssueCouponModal" class="modal-overlay" @click.self="showIssueCouponModal = false">
    <div class="modal-container modal-sm">
      <div class="modal-header">
        <h3 class="modal-title">發放優惠券</h3>
        <button class="modal-close" @click="showIssueCouponModal = false">
          <i class="ri-close-line"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">選擇優惠券模板 <span class="required">*</span></label>
          <select v-model="issueCouponForm.templateId" class="form-control">
            <option value="">請選擇模板</option>
            <option v-for="t in couponTemplates" :key="t.id" :value="t.id">
              {{ t.templateName }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">發放數量</label>
          <input
            v-model.number="issueCouponForm.quantity"
            type="number"
            min="1"
            max="10"
            class="form-control"
          />
        </div>
        <div class="form-group">
          <label class="form-label">發放備註</label>
          <input
            v-model="issueCouponForm.reason"
            type="text"
            class="form-control"
            placeholder="選填"
          />
        </div>
        <div v-if="hasMember" class="issue-target-hint">
          <i class="ri-information-line"></i>
          此好友已綁定會員，票券將發放至會員帳號
        </div>
        <div v-else class="issue-target-hint">
          <i class="ri-information-line"></i>
          此好友尚未綁定會員，票券將以 LINE User ID 記錄
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="showIssueCouponModal = false">取消</button>
        <button
          class="btn btn-primary"
          :disabled="issuingCoupon || !issueCouponForm.templateId"
          @click="submitIssueCoupon"
        >
          {{ issuingCoupon ? '發放中...' : '確定發放' }}
        </button>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { usePageTitle } from '@/composables/usePageTitle.js';
import lineBindingApi from '@/api/modules/lineBinding.js';
import { tagService } from '@/api/modules/tags.js';
import { couponService } from '@/api/modules/coupons.js';
import { showSuccess, showError } from '@/utils/swal.js';
import Swal from 'sweetalert2';
import CouponDetailModal from '@/components/CouponDetailModal.vue';

export default {
  name: 'LineBindingDetail',
  components: { CouponDetailModal },
  setup() {
    const { pageTitle } = usePageTitle('好友綁定詳情');
    return { pageTitle };
  },
  data() {
    return {
      loading: false,
      error: null,
      binding: null,
      refreshing: false,
      activeTab: 'basic',
      // Tags states
      memberTags: [],
      tagsLoading: false,
      tagsError: null,
      showAddTagModal: false,
      selectedTagIds: [],
      addingTag: false,
      availableTags: [],
      filteredAvailableTags: [],
      tagSearchQuery: '',
      activeTagTab: 'all',
      // Coupons states
      coupons: [],
      couponsLoading: false,
      couponsError: null,
      couponPagination: null,
      couponFilterStatus: '',
      // Issue coupon states
      showIssueCouponModal: false,
      issuingCoupon: false,
      couponTemplates: [],
      issueCouponForm: { templateId: '', quantity: 1, reason: '' },
      // Coupon detail modal
      showCouponDetailModal: false,
      selectedCoupon: null,
      deleting: false
    };
  },
  computed: {
    hasMember() {
      return !!(this.binding?.memberId || (this.binding?.member && this.binding.member.id));
    },
    memberId() {
      if (this.binding?.memberId) {
        return this.binding.memberId;
      }
      if (this.binding?.member && this.binding.member.id) {
        return this.binding.member.id;
      }
      return null;
    },
    // 使用 LINE User ID 來查詢標籤（無論是否綁定會員）
    canLoadTags() {
      return !!(this.binding?.lineUserId && this.binding?.messagingChannelId);
    }
  },
  mounted() {
    this.loadBinding();
  },
  watch: {
    binding: {
      handler(newVal) {
        // 當 binding 載入完成後，如果有 LINE User ID，則可以載入標籤
        if (newVal && this.canLoadTags) {
          // 延遲載入，確保 computed 屬性已更新
          this.$nextTick(() => {
            if (this.canLoadTags) {
              // 不自動載入，等用戶切換到標籤 Tab 時再載入
            }
          });
        } else {
          // 如果無法載入標籤，清空標籤列表
          this.memberTags = [];
          this.tagsError = null;
        }
      },
      immediate: true
    },
    activeTab: {
      handler(newTab) {
        if (newTab === 'tags' && this.canLoadTags && !this.tagsLoading && !this.tagsError) {
          if (this.memberTags.length === 0) {
            this.loadMemberTags();
          }
        }
        if (newTab === 'coupons' && this.binding?.lineUserId && !this.couponsLoading) {
          if (this.coupons.length === 0 && !this.couponsError) {
            this.loadCoupons(1);
          }
          if (this.couponTemplates.length === 0) {
            this.loadCouponTemplates();
          }
        }
      }
    }
  },
  methods: {
    async loadBinding() {
      this.loading = true;
      this.error = null;
      try {
        const bindingId = this.$route.params.id;
        const response = await lineBindingApi.get(bindingId);
        
        if (response && response.success) {
          this.binding = response.data;
        } else {
          this.error = '找不到綁定記錄';
        }
      } catch (error) {
        console.error('載入綁定記錄失敗:', error);
        this.error = error?.response?.data?.message || error?.message || '載入失敗，請稍後再試';
      } finally {
        this.loading = false;
      }
    },

    handleBack() {
      this.$router.push({ name: 'ClientLineBindingList' });
    },

    async handleDeleteRecord() {
      if (!this.binding?.id || this.deleting) return;

      const nameHint = this.binding.lineDisplayName || this.binding.lineUserId || this.binding.id;
      const r = await Swal.fire({
        title: '確定刪除此筆好友紀錄？',
        html:
          `<p>對象：<strong>${this.escapeHtml(nameHint)}</strong></p>` +
          '<p class="text-start small text-muted">僅刪除本系統資料庫中的 line_follow 與關聯指標（掃碼工作階段、分享邀請、該通道之 LINE 好友標籤）。<strong>不會</strong>刪除會員，也<strong>不會</strong>在 LINE 官方解除好友。</p>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定刪除',
        cancelButtonText: '取消',
        focusCancel: true,
        confirmButtonColor: '#dc3545'
      });

      if (!r.isConfirmed) return;

      this.deleting = true;
      try {
        const response = await lineBindingApi.remove(this.binding.id);
        if (response && response.success) {
          await showSuccess('已刪除', '好友綁定紀錄已從本系統移除');
          this.$router.push({ name: 'ClientLineBindingList' });
        } else {
          await showError('刪除失敗', response?.message || '請稍後再試');
        }
      } catch (error) {
        console.error('刪除好友紀錄失敗:', error);
        const msg = error?.response?.data?.message || error?.message || '刪除失敗，請稍後再試';
        await showError('刪除失敗', msg);
      } finally {
        this.deleting = false;
      }
    },

    escapeHtml(text) {
      const s = String(text ?? '');
      return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    async handleRefreshProfile() {
      if (this.refreshing) return;
      
      this.refreshing = true;
      try {
        const response = await lineBindingApi.refreshProfile(this.binding.id);
        
        if (response && response.success) {
          // 更新本地資料
          this.binding.lineDisplayName = response.data.lineDisplayName;
          this.binding.linePictureUrl = response.data.linePictureUrl;
          this.binding.profileUpdatedAt = response.data.profileUpdatedAt;
          
          await showSuccess('成功', 'LINE Profile 已更新');
        } else {
          await showError('更新失敗', response?.message || '刷新失敗，請稍後再試');
        }
      } catch (error) {
        console.error('刷新 Profile 失敗:', error);
        const msg = error?.response?.data?.message || error?.message || '刷新失敗，請稍後再試';
        await showError('無法取得 LINE 顯示名稱', msg);
      } finally {
        this.refreshing = false;
      }
    },

    formatSource(source) {
      const sourceMap = {
        'qrcode': 'QR Code',
        'campaign': '活動',
        'manual': '手動綁定',
        'webhook': 'Webhook',
        'short_url': '短網址',
        'follow': 'LINE 加好友',
        'unknown': '未標記（舊資料）',
        'liff_binding': 'LIFF 綁定（既有會員）',
        'liff_registration': 'LIFF 註冊',
        'liff': 'LIFF',
        'line_sync': 'LINE 同步',
        'import': 'CSV 匯入'
      };
      return sourceMap[source] || source || '-';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    getFriendStatusText(isFollowing) {
      if (isFollowing === true) return '好友';
      if (isFollowing === false) return '封鎖（非好友）';
      return '-';
    },

    getFriendStatusBadgeClass(isFollowing) {
      return isFollowing ? 'badge badge-success' : 'badge badge-danger';
    },

    getPushStatusText(binding) {
      if (binding?.isFollowing === false) return '—（已非好友）';
      if (binding?.isPushBlocked) return '不可達（推播失敗偵測）';
      return '正常';
    },

    getPushStatusBadgeClass(binding) {
      if (binding?.isFollowing === false) return 'badge badge-secondary';
      if (binding?.isPushBlocked) return 'badge badge-warning';
      return 'badge badge-success';
    },

    async loadMemberTags() {
      // 使用 LINE User ID 查詢標籤（透過綁定 ID）
      if (!this.binding?.id || !this.canLoadTags) {
        this.memberTags = [];
        this.tagsError = null;
        return;
      }
      
      this.tagsLoading = true;
      this.tagsError = null;

      try {
        // 使用 LINE 綁定 API 查詢標籤（基於 LINE User ID）
        const response = await lineBindingApi.getTags(this.binding.id);
        
        if (response && response.success && response.data) {
          // 轉換標籤格式以符合前端顯示需求
          this.memberTags = (response.data || []).map(tag => ({
            id: tag.id,
            tagId: tag.tagId,
            tagName: tag.tagName || tag.tag_name,
            tagSource: tag.tagSource || tag.tag_source || 'manual',
            tagColor: tag.tagColor || tag.tag_color,
            tagIcon: tag.tagIcon || tag.tag_icon,
            intensity: tag.intensity || 1,
            expiryDate: tag.expiryDate || tag.expiry_date,
            assignedAt: tag.assignedAt || tag.assigned_at
          }));
        } else {
          this.tagsError = response?.message || '獲取標籤列表失敗';
        }
      } catch (err) {
        console.error('獲取標籤列表失敗:', err);
        this.tagsError = err.response?.data?.message || err.message || '獲取標籤列表失敗';
        this.memberTags = [];
      } finally {
        this.tagsLoading = false;
      }
    },

    async loadAvailableTags() {
      try {
        const response = await tagService.getTags({ isActive: true });
        if (response && response.success && response.data) {
          this.availableTags = (Array.isArray(response.data) ? response.data : [])
            .filter(tag => tag.tagSource !== 'system');
          this.filteredAvailableTags = this.availableTags;
        }
      } catch (err) {
        console.error('獲取標籤列表失敗:', err);
      }
    },

    filterTags() {
      const query = this.tagSearchQuery.toLowerCase().trim();
      let tags = this.availableTags;
      
      if (this.activeTagTab !== 'all') {
        tags = tags.filter(tag => tag.tagSource === this.activeTagTab);
      }
      
      if (query) {
        tags = tags.filter(tag => 
          tag.tagName.toLowerCase().includes(query) || 
          (tag.description && tag.description.toLowerCase().includes(query))
        );
      }
      
      this.filteredAvailableTags = tags;
    },

    toggleTagSelection(tagId) {
      const tag = this.availableTags.find(t => t.id === tagId);
      if (!tag) return;
      
      if (tag.tagSource !== 'manual' && this.isTagAlreadyAdded(tagId)) {
        return;
      }

      const index = this.selectedTagIds.indexOf(tagId);
      if (index > -1) {
        this.selectedTagIds.splice(index, 1);
      } else {
        this.selectedTagIds.push(tagId);
      }
    },

    removeFromSelection(tagId) {
      const index = this.selectedTagIds.indexOf(tagId);
      if (index > -1) {
        this.selectedTagIds.splice(index, 1);
      }
    },

    getTagName(tagId) {
      const tag = this.availableTags.find(t => t.id === tagId);
      return tag ? tag.tagName : '';
    },

    getTagIcon(tagId) {
      const tag = this.availableTags.find(t => t.id === tagId);
      return tag ? tag.tagIcon : 'ri-price-tag-3-line';
    },

    getTagColor(tagId) {
      const tag = this.availableTags.find(t => t.id === tagId);
      return tag ? tag.tagColor : '#000000';
    },

    isTagAlreadyAdded(tagId) {
      const tag = this.availableTags.find(t => t.id === tagId);
      if (!tag) return false;
      
      if (tag.tagSource === 'manual') {
        return false;
      }
      
      return this.memberTags.some(memberTag => memberTag.tagId === tagId);
    },

    async openAddTagModal() {
      if (!this.canLoadTags) {
        await showError('錯誤', '無法載入標籤：缺少 LINE User ID 或 Messaging Channel ID');
        return;
      }
      this.showAddTagModal = true;
      this.tagSearchQuery = '';
      this.selectedTagIds = [];
      await this.loadAvailableTags();
    },

    async addMultipleTags() {
      if (!this.binding?.id || !this.canLoadTags) return;
      if (this.selectedTagIds.length === 0) {
        await showError('驗證失敗', '請至少選擇一個標籤');
        return;
      }

      this.addingTag = true;

      try {
        let successCount = 0;
        let failCount = 0;
        const errors = [];

        for (const tagId of this.selectedTagIds) {
          try {
            // 使用 LINE 綁定 API 新增標籤（基於 LINE User ID）
            const response = await lineBindingApi.addTag(this.binding.id, tagId);
            if (response && response.success) {
              successCount++;
            } else {
              failCount++;
              const tagName = this.getTagName(tagId);
              errors.push(`${tagName}: ${response?.message || '新增失敗'}`);
            }
          } catch (err) {
            failCount++;
            const tagName = this.getTagName(tagId);
            errors.push(`${tagName}: ${err.response?.data?.message || err.message || '新增失敗'}`);
          }
        }

        if (successCount > 0 && failCount === 0) {
          await showSuccess('成功', `已成功新增 ${successCount} 個標籤`);
          this.showAddTagModal = false;
          this.selectedTagIds = [];
          this.tagSearchQuery = '';
          await this.loadMemberTags();
        } else if (successCount > 0 && failCount > 0) {
          await Swal.fire({
            icon: 'warning',
            title: '部分成功',
            html: `
              <p>成功新增 ${successCount} 個標籤</p>
              <p>失敗 ${failCount} 個標籤</p>
              <div style="text-align: left; margin-top: 10px;">
                ${errors.map(err => `<div>• ${err}</div>`).join('')}
              </div>
            `,
            confirmButtonText: '確定'
          });
          this.showAddTagModal = false;
          this.selectedTagIds = [];
          this.tagSearchQuery = '';
          await this.loadMemberTags();
        } else {
          await Swal.fire({
            icon: 'error',
            title: '新增失敗',
            html: `
              <div style="text-align: left;">
                ${errors.map(err => `<div>• ${err}</div>`).join('')}
              </div>
            `,
            confirmButtonText: '確定'
          });
        }
      } catch (err) {
        console.error('批次新增標籤失敗:', err);
        await showError('新增失敗', err.message || '批次新增標籤失敗');
      } finally {
        this.addingTag = false;
      }
    },

    async removeTag(tagId) {
      if (!this.binding?.id || !this.canLoadTags) return;
      
      try {
        const response = await lineBindingApi.removeTag(this.binding.id, tagId);

        if (response && response.success) {
          await showSuccess('成功', '標籤移除成功');
          await this.loadMemberTags();
        } else {
          await showError('移除失敗', response?.message || '標籤移除失敗');
        }
      } catch (err) {
        console.error('移除標籤失敗:', err);
        await showError('移除失敗', err.response?.data?.message || err.message || '標籤移除失敗');
      }
    },

    async loadCoupons(page = 1) {
      if (!this.binding?.lineUserId) return;

      this.couponsLoading = true;
      this.couponsError = null;

      try {
        const params = { page, pageSize: 20 };
        if (this.couponFilterStatus) {
          params.status = this.couponFilterStatus;
        }

        const response = await couponService.getCouponsByLineUser(this.binding.lineUserId, params);

        if (response && response.success && response.data) {
          this.coupons = response.data.items || [];
          this.couponPagination = response.data.pagination;
        } else {
          this.couponsError = response?.message || '載入票券失敗';
        }
      } catch (err) {
        console.error('載入票券失敗:', err);
        this.couponsError = err.response?.data?.message || err.message || '載入票券失敗';
      } finally {
        this.couponsLoading = false;
      }
    },

    viewCouponDetail(coupon) {
      this.selectedCoupon = coupon;
      this.showCouponDetailModal = true;
    },

    async handleCancelCoupon(coupon) {
      const result = await Swal.fire({
        title: '確認取消票券',
        text: `確定要取消票券 ${coupon.couponCode}？取消後票券將無法使用。`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定取消',
        cancelButtonText: '返回',
        confirmButtonColor: '#dc3545'
      });
      if (!result.isConfirmed) return;

      try {
        const response = await couponService.cancelCoupon(coupon.id, '管理員手動取消');
        if (response && response.success) {
          await showSuccess('成功', '票券已取消');
          await this.loadCoupons(1);
        } else {
          await showError('失敗', response?.message || '取消失敗');
        }
      } catch (err) {
        await showError('失敗', err.response?.data?.message || err.message || '取消失敗');
      }
    },

    async handleDeleteCoupon(coupon) {
      const result = await Swal.fire({
        title: '確認刪除票券',
        html: `確定要刪除票券 <strong>${coupon.couponCode}</strong>？<br>此操作不可逆，但記錄將保留在系統中（狀態標記為已刪除）。`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定刪除',
        cancelButtonText: '返回',
        confirmButtonColor: '#dc3545'
      });
      if (!result.isConfirmed) return;

      try {
        const response = await couponService.deleteCouponIssue(coupon.id, '發放錯誤，手動刪除');
        if (response && response.success) {
          await showSuccess('成功', '票券已刪除');
          await this.loadCoupons(1);
        } else {
          await showError('失敗', response?.message || '刪除失敗');
        }
      } catch (err) {
        await showError('失敗', err.response?.data?.message || err.message || '刪除失敗');
      }
    },

    async loadCouponTemplates() {
      try {
        const response = await couponService.getTemplates({ pageSize: 100, status: 'active' });
        if (response && response.success && response.data) {
          this.couponTemplates = response.data.items || [];
        }
      } catch (err) {
        console.error('載入優惠券模板失敗:', err);
      }
    },

    async submitIssueCoupon() {
      if (!this.issueCouponForm.templateId) {
        await showError('驗證失敗', '請選擇優惠券模板');
        return;
      }

      this.issuingCoupon = true;
      try {
        const payload = {
          templateId: this.issueCouponForm.templateId,
          quantity: this.issueCouponForm.quantity,
          reason: this.issueCouponForm.reason || '手動發放'
        };

        if (this.memberId) {
          payload.memberId = this.memberId;
        } else {
          payload.lineUserId = this.binding.lineUserId;
        }

        const response = await couponService.issueCoupon(payload);

        if (response && response.success) {
          await showSuccess('成功', '優惠券發放成功');
          this.showIssueCouponModal = false;
          this.issueCouponForm = { templateId: '', quantity: 1, reason: '' };
          await this.loadCoupons(1);
        } else {
          await showError('發放失敗', response?.message || '優惠券發放失敗');
        }
      } catch (err) {
        console.error('發放優惠券失敗:', err);
        await showError('發放失敗', err.response?.data?.message || err.message || '發放失敗');
      } finally {
        this.issuingCoupon = false;
      }
    },

    getCouponStatusText(status) {
      const map = {
        unused: '未使用',
        transferring: '轉送中',
        transferred_out: '已轉出',
        used: '已使用',
        expired: '已過期',
        cancelled: '已取消',
        deleted: '已刪除',
        transferred: '已轉讓'
      };
      return map[status] || status;
    },

    getCouponStatusClass(status) {
      const map = {
        unused: 'badge badge-success',
        transferring: 'badge badge-warning',
        transferred_out: 'badge badge-info',
        used: 'badge badge-primary',
        expired: 'badge badge-secondary',
        cancelled: 'badge badge-warning',
        deleted: 'badge badge-danger',
        transferred: 'badge badge-info'
      };
      return map[status] || 'badge badge-secondary';
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }
.header-actions .btn {
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}
.header-actions .btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}
.header-actions .btn-secondary:hover { background: #e5e7eb; }

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.loading-container,
.error-container {
  text-align: center;
  padding: 40px 20px;
}

.loading-container .spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1890ff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #ff4d4f;
  margin-bottom: 16px;
}

.detail-container {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.detail-section {
  margin-bottom: 32px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section h3 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item label {
  font-size: 14px;
  font-weight: 500;
  color: #666;
}

.detail-item div {
  font-size: 15px;
  color: #333;
  word-break: break-word;
}

.detail-item code {
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #e83e8c;
  word-break: break-all;
}

.avatar-image {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e0e0e0;
}

.tags-display {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-badge {
  display: inline-block;
  padding: 4px 12px;
  background: #f0f0f0;
  border-radius: 16px;
  font-size: 13px;
  color: #666;
}

.text-muted {
  color: #999;
  font-style: italic;
}

.badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
}

.badge-success {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.badge-danger {
  background: #fff2f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}

.profile-field {
  display: flex;
  align-items: center;
  gap: 12px;
}

.refresh-btn {
  padding: 4px 12px;
  font-size: 13px;
  color: #1890ff;
  background: transparent;
  border: 1px solid #1890ff;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.refresh-btn:hover:not(:disabled) {
  background: #1890ff;
  color: white;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-btn i {
  font-size: 14px;
}

.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.badge-secondary {
  background: #f5f5f5;
  color: #666;
  border: 1px solid #d9d9d9;
}

/* Tabs Navigation */
.tabs-nav {
  display: flex;
  background: white;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  margin-bottom: 0;
}

.tab-btn {
  flex: 1;
  padding: 16px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}

.tab-btn:hover {
  background: #f8f9fa;
  color: #333;
}

.tab-btn.active {
  color: #667eea;
  border-bottom-color: #667eea;
  background: #f8f9ff;
}

.tab-btn i {
  font-size: 18px;
}

/* Tabs Content */
.tabs-content {
  background: white;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 400px;
}

.tab-pane {
  padding: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

/* Tags List */
.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
}

.tag-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  min-width: 200px;
}

.tag-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.tag-icon {
  font-size: 18px;
}

.tag-name {
  font-weight: 600;
  font-size: 14px;
}

.tag-expiry {
  font-size: 12px;
  color: #f57c00;
  padding: 2px 6px;
  background: #fff9c4;
  border-radius: 4px;
}

.tag-intensity {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 22px;
  padding: 0 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 12px;
  font-weight: 700;
  border-radius: 11px;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  cursor: help;
  transition: all 0.2s;
}

.tag-intensity:hover {
  transform: scale(1.1);
  box-shadow: 0 3px 6px rgba(102, 126, 234, 0.4);
}

.tag-type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.manual-type {
  background-color: #fff3e0;
  color: #e65100;
}

.auto-type {
  background-color: #e3f2fd;
  color: #1565c0;
}

.system-type {
  background-color: #f3e5f5;
  color: #6a1b9a;
}

.btn-remove {
  background: transparent;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-remove:hover {
  background: #fee;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px 20px;
}

.spinner-sm {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 12px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-md {
  max-width: 600px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f8f9fa;
  color: #666;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.selected-tags-container {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.selected-tag-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #667eea;
  border-radius: 16px;
  font-size: 13px;
  color: #667eea;
}

.remove-tag-btn {
  background: transparent;
  border: none;
  color: #667eea;
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-tag-btn:hover {
  color: #dc3545;
}

.tag-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.tag-tab {
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tag-tab:hover {
  color: #333;
  background: #f8f9fa;
}

.tag-tab.active {
  color: #667eea;
  border-bottom-color: #667eea;
}

.tag-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s;
}

.tag-list-item:last-child {
  border-bottom: none;
}

.tag-list-item:hover:not(.disabled) {
  background: #f8f9fa;
}

.tag-list-item.selected {
  background: #e7f3ff;
}

.tag-list-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f8f9fa;
}

.tag-list-item.manual-tag:not(.disabled) {
  border-left: 3px solid transparent;
  transition: all 0.2s;
}

.tag-list-item.manual-tag:not(.disabled):hover {
  background-color: #fff3e0;
  border-left-color: #ff9800;
}

.check-icon {
  color: #667eea;
  font-size: 20px;
}

.tag-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 6px;
}

.manual-badge {
  background-color: #fff3e0;
  color: #e65100;
}

.auto-badge {
  background-color: #e3f2fd;
  color: #1565c0;
}

.system-badge {
  background-color: #f3e5f5;
  color: #6a1b9a;
}

.temp-badge {
  background-color: #fff9c4;
  color: #f57c00;
}

.added-badge {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #667eea;
  color: white;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  margin-left: 6px;
}

.coupon-filter {
  margin-bottom: 16px;
  display: flex;
  gap: 12px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 0 4px;
}

.modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.modal-sm {
  max-width: 480px;
}

.action-buttons {
  display: flex;
  gap: 6px;
  justify-content: flex-start;
  min-width: 195px;
}

.cancel-reason-text {
  font-size: 12px;
  color: #888;
  margin-top: 3px;
  white-space: normal;
  word-break: break-all;
}

.cancel-text {
  color: #dc3545;
}

.detail-section {
  margin-bottom: 16px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-subtitle {
  font-size: 14px;
  font-weight: 600;
  color: #555;
  margin: 0 0 10px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 0;
  font-size: 14px;
}

.detail-label {
  color: #888;
  white-space: nowrap;
  min-width: 80px;
}

.btn-warning {
  background: #ffc107;
  color: #212529;
  border: none;
  padding: 6px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.btn-warning:hover {
  background: #e0a800;
}

.btn-outline-danger {
  background: transparent;
  color: #dc3545;
  border: 1px solid #dc3545;
  padding: 6px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.btn-outline-danger:hover {
  background: #dc3545;
  color: #fff;
}

.issue-target-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f0f7ff;
  border: 1px solid #b3d4f5;
  border-radius: 6px;
  font-size: 13px;
  color: #2c6fad;
  margin-top: 4px;
}

.issue-target-hint i {
  font-size: 15px;
  flex-shrink: 0;
}

.required {
  color: #dc3545;
}

.form-control-sm {
  padding: 6px 10px;
  font-size: 13px;
  width: auto;
  min-width: 140px;
}

.coupons-table-wrapper {
  margin-top: 8px;
  overflow-x: auto;
}

.coupons-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
}

.coupons-table thead {
  background: #f8f9fa;
}

.coupons-table th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #444;
  font-size: 13px;
  border-bottom: 2px solid #e9ecef;
}

.coupons-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  color: #333;
}

.coupons-table tbody tr:hover {
  background: #f8f9fa;
}

.coupon-code {
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  color: #333;
  letter-spacing: 0.5px;
}

.coupon-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
}

.pagination-info {
  color: #666;
  font-size: 13px;
}

.badge-primary {
  background: #e8f4fd;
  color: #0070c0;
  border: 1px solid #9dcfef;
}

.badge-warning {
  background: #fff8e1;
  color: #f57c00;
  border: 1px solid #ffe082;
}

.badge-info {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-primary {
  background-color: #667eea;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-primary:hover {
  background-color: #5568d3;
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background-color: #5a6268;
}
</style>

<style>
.page-header-bindings-detail {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}
.sidebar.collapsed ~ .main-content .page-header-bindings-detail {
  left: 64px;
}
@media (max-width: 768px) {
  .page-header-bindings-detail {
    left: 0 !important;
    top: 56px;
  }
}
</style>
